import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Typography, IconButton } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { ArrowLeft, ArrowRight, Pause, Play } from "iconsax-react";

export default function PeekCarousel({ eyebrow, heading, description, slides }) {
  const swiperRef = useRef(null);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(() => {
      swiperRef.current?.slideNext();
    }, 3500);
    return () => clearInterval(timer);
  }, [playing]);

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md" sx={{ textAlign: { xs: "left", sm: "center" }, mb: { xs: 3, md: 4 } }}>
        {eyebrow && (
          <Typography
            color="gray"
            letterSpacing={2}
            fontFamily="KIAbodyLatinbasic_Bold"
            sx={{ fontSize: "clamp(12px, 1.6vw, 14px)" }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography
          fontFamily="KIAbodyLatinbasic_Bold"
          sx={{ fontSize: "clamp(24px, 4vw, 34px)", my: 1 }}
        >
          {heading}
        </Typography>
        {description && (
          <Typography
            fontFamily="KIAbodyLatinbasic_Regular"
            color="text.secondary"
            sx={{ fontSize: "clamp(14px, 1.8vw, 16px)" }}
          >
            {description}
          </Typography>
        )}
      </Container>

      <Box sx={{ px: { xs: 2, md: 6 } }}>
        <Swiper
          modules={[Navigation]}
          centeredSlides
          loop
          spaceBetween={20}
          slidesPerView={1.15}
          breakpoints={{
            600: { slidesPerView: 1.6, spaceBetween: 24 },
            960: { slidesPerView: 2.4, spaceBetween: 28 },
          }}
          onSwiper={(s) => (swiperRef.current = s)}
          onSlideChange={(s) => setActive(s.realIndex)}
          style={{ paddingBottom: 4 }}
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={index}>
              {({ isActive }) => (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: { xs: 220, sm: 320, md: 420 },
                    borderRadius: "6px",
                    overflow: "hidden",
                    opacity: isActive ? 1 : 0.4,
                    transform: isActive ? "scale(1)" : "scale(0.94)",
                    transition: "opacity 0.4s ease, transform 0.4s ease",
                  }}
                >
                  {slide.image.split("?")[0].toLowerCase().endsWith(".mp4") ? (
                    <Box
                      component="video"
                      src={slide.image}
                      autoPlay={isActive}
                      loop
                      muted
                      playsInline
                      preload={isActive ? "auto" : "metadata"}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Box
                      component="img"
                      loading="lazy"
                      decoding="async"
                      src={slide.image}
                      alt={slide.title}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  )}
                  {(slide.title || slide.subtitle) && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        p: { xs: 2, md: 3 },
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0))",
                        color: "#fff",
                      }}
                    >
                      {slide.title && (
                        <Typography
                          fontFamily="KIAbodyLatinbasic_Bold"
                          sx={{ fontSize: "clamp(14px, 1.8vw, 18px)" }}
                        >
                          {slide.title}
                        </Typography>
                      )}
                      {slide.subtitle && (
                        <Typography
                          fontFamily="KIAbodyLatinbasic_Regular"
                          sx={{ fontSize: "clamp(12px, 1.5vw, 14px)" }}
                        >
                          {slide.subtitle}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          mt: 3,
        }}
      >
        <IconButton
          size="small"
          onClick={() => swiperRef.current?.slidePrev()}
          sx={{ border: "1px solid #000", p: 1 }}
        >
          <ArrowLeft size={18} />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {slides.map((_, index) => (
            <Box
              key={index}
              onClick={() => swiperRef.current?.slideToLoop(index)}
              sx={{
                cursor: "pointer",
                height: "4px",
                borderRadius: "2px",
                backgroundColor: index === active ? "#000" : "#ccc",
                width: index === active ? "22px" : "8px",
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </Box>

        <IconButton
          size="small"
          onClick={() => swiperRef.current?.slideNext()}
          sx={{ border: "1px solid #000", p: 1 }}
        >
          <ArrowRight size={18} />
        </IconButton>

        <IconButton
          size="small"
          onClick={() => setPlaying((p) => !p)}
          sx={{ border: "1px solid #000", p: 1, ml: 1 }}
        >
          {playing ? <Pause size={18} /> : <Play size={18} />}
        </IconButton>
      </Box>
    </Box>
  );
}
